import { ChevronLeft, SaveOutlined } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { useParams } from 'react-router'
import { orderType } from '../types'
import './EditOrder.css'
import { useClassList, useDivisionList, useEndUserList, useInvoicingPlatformList, useRepCodes, useUser } from '../hooks'
import { calculateGp, CurrencyInput, DatePicker, getRepCodeName } from '../helper'
import { dpasRatingOptions, govPayTerms, productTypes, securityRequirementOptions, setAsides } from '../lists'
import dayjs from 'dayjs'
import { getData } from 'country-list'
import { fieldLengths } from '../fieldLengths'

export default function EditOrder({
  originalOrder,
  open,
  setOpen,
  setOriginalOrder,
  onBack,
}: {
  originalOrder: orderType
  open: boolean
  setOpen: (open: boolean) => void
  setOriginalOrder: (order: orderType) => void
  onBack: () => void
}) {
  const { orderID } = useParams<{ orderID: string }>()
  const user = useUser()
  const invoicingPlatforms = useInvoicingPlatformList()
  const classList = useClassList()
  const divisionList = useDivisionList()
  const endUserList = useEndUserList()
  const [repCodes] = useRepCodes()

  const [saved, setSaved] = useState<'saving' | 'error' | 'success' | ''>('')
  const [newOrder, setNewOrder] = useState<orderType>({} as orderType)
  const [purchaseVehicle, setPurchaseVehicle] = useState<string>('')
  const [licenseSameAsReceiver, setLicenseSameAsReceiver] = useState(false)

  useEffect(() => {
    setNewOrder(structuredClone(originalOrder))
  }, [originalOrder])

  const updateOrderAndGrossMargin = (changes: Partial<orderType>) => {
    setNewOrder(prev => {
      const next = { ...prev, ...changes }
      return {
        ...next,
        GrossMargin: calculateGp(
          next.OrderTotal ?? 0,
          next.TotalCost ?? 0,
          next.PurchaseVehicle ?? '',
          next.IsCreditCard || next.GovPaymentTerms === 'Credit Card',
        ),
      }
    })
  }

  const countryList = useMemo(() => {
    return getData().sort((a, b) => (a.code === 'US' ? -1 : b.code === 'US' ? 1 : a.name.localeCompare(b.name)))
  }, [])

  const saveEdits = () => {
    setSaved('saving')
    axios
      .patch(`/order/${orderID}/editorder`, {
        originalOrder: originalOrder,
        newOrder: {
          ...newOrder,
          ReceiverContactPhone: newOrder.ReceiverContactPhone ? newOrder.ReceiverContactPhone.trim() : null,
          LicenseContactPhone: newOrder.LicenseContactPhone ? newOrder.LicenseContactPhone.trim() : null,
        },
        editedBy: user.email,
      })
      .then(() => {
        setSaved('success')
        setOriginalOrder(structuredClone(newOrder))
        onBack()
      })
      .catch(err => {
        setSaved('error')
        if (err.response) {
          // received error code other than 2xx
          console.error(err.response)
        } else if (err.request) {
          // no response received
          console.error(err.request)
        } else {
          // request was not made
          console.error(err.message)
        }
      })
  }

  return (
    <>
      <Dialog fullScreen open={open} onClose={() => setOpen(false)}>
        <div className="edit-order-header">
          <div>
            <Button variant="contained" startIcon={<ChevronLeft />} onClick={onBack}>
              Back
            </Button>
            <Typography variant="h6">Edit Order</Typography>
          </div>
          <Button variant="contained" startIcon={<SaveOutlined />} onClick={saveEdits} loading={saved === 'saving'}>
            Save
          </Button>
        </div>
        <Divider flexItem />
        <div className="edit-order-content">
          <Accordion defaultExpanded disableGutters>
            <AccordionSummary>
              <Typography variant="h6">Overview</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing="24px">
                <Grid size={12 / 5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Quote Number"
                    value={newOrder.QuoteNumber ?? ''}
                    onChange={e => setNewOrder(prev => ({ ...prev, QuoteNumber: e.target.value }))}
                    slotProps={{ htmlInput: { maxLength: fieldLengths.orders.quoteNumber } }}
                  />
                </Grid>
                <Grid size={12 / 5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Customer PO Number"
                    value={newOrder.CustomerPO ?? ''}
                    onChange={e => setNewOrder(prev => ({ ...prev, CustomerPO: e.target.value }))}
                    slotProps={{ htmlInput: { maxLength: fieldLengths.orders.customerPO } }}
                  />
                </Grid>
                <Grid size={12 / 5}>
                  <FormControlLabel
                    control={<Checkbox />}
                    label="Credit Card Order"
                    checked={newOrder.IsCreditCard ?? false}
                    onChange={(_e, checked) =>
                      updateOrderAndGrossMargin({
                        IsCreditCard: checked,
                        ...(checked && { GovPaymentTerms: 'Credit Card' }),
                      })
                    }
                  />
                </Grid>
                <Grid size={12 / 5}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={repCodes.filter(code => code.IsActive).sort((a, b) => a.RepCode.localeCompare(b.RepCode))}
                    value={repCodes.find(code => code.RepCode === newOrder.RepCode) ?? null}
                    onChange={(_e, value) => setNewOrder(prev => ({ ...prev, RepCode: value?.RepCode ?? '' }))}
                    getOptionLabel={option => `${option.RepCode} ${getRepCodeName(option)}`}
                    renderInput={params => <TextField {...params} label="Rep Code" />}
                  />
                </Grid>
                <Grid size={12 / 5}>
                  <CurrencyInput
                    fullWidth
                    size="small"
                    value={newOrder.OrderTotal ?? 0}
                    onChange={value => updateOrderAndGrossMargin({ OrderTotal: value })}
                    label="Order Total"
                  />
                </Grid>
                <Grid size={12 / 5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Sales Order Number"
                    value={newOrder.SalesOrderNumber ?? ''}
                    onChange={e => setNewOrder(prev => ({ ...prev, SalesOrderNumber: e.target.value }))}
                    slotProps={{ htmlInput: { maxLength: fieldLengths.orders.salesOrderNumber } }}
                  />
                </Grid>
                <Grid size={12 / 5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Gov. Award #"
                    value={newOrder.GovAwardNumber ?? ''}
                    onChange={e => setNewOrder(prev => ({ ...prev, GovAwardNumber: e.target.value }))}
                    slotProps={{ htmlInput: { maxLength: fieldLengths.orders.govAwardNumber } }}
                  />
                </Grid>
                <Grid size={12 / 5}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="Customer Payment Terms"
                    value={newOrder.GovPaymentTerms ?? ''}
                    onChange={e => updateOrderAndGrossMargin({ GovPaymentTerms: e.target.value })}
                  >
                    {govPayTerms.map(term => (
                      <MenuItem key={term} value={term}>
                        {term}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={12 / 5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="STN"
                    value={newOrder.STN ?? ''}
                    onChange={e => setNewOrder(prev => ({ ...prev, STN: e.target.value }))}
                    slotProps={{ htmlInput: { maxLength: fieldLengths.orders.stn } }}
                  />
                </Grid>
                <Grid size={12 / 5}>
                  <CurrencyInput
                    fullWidth
                    size="small"
                    value={newOrder.TotalCost ?? 0}
                    onChange={value => updateOrderAndGrossMargin({ TotalCost: value })}
                    label="Total Cost"
                  />
                </Grid>
                <Grid size={12 / 5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Salesforce Opportunity"
                    value={newOrder.Opportunity ?? ''}
                    onChange={e => setNewOrder(prev => ({ ...prev, Opportunity: e.target.value }))}
                    slotProps={{ htmlInput: { maxLength: fieldLengths.orders.opportunity } }}
                  />
                </Grid>
                <Grid size={12 / 5}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="DPAS Rating"
                    value={newOrder.DPASRating ?? ''}
                    onChange={e => setNewOrder(prev => ({ ...prev, DPASRating: e.target.value }))}
                  >
                    {dpasRatingOptions.map(option => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={12 / 5}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="Invoicing Platform"
                    value={newOrder.InvoicingPlatform ?? ''}
                    onChange={e => setNewOrder(prev => ({ ...prev, InvoicingPlatform: e.target.value }))}
                  >
                    {invoicingPlatforms.map(platform => (
                      <MenuItem key={platform} value={platform}>
                        {platform}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={12 / 5}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="Purchase Vehicle"
                    value={newOrder.PurchaseVehicle ?? ''}
                    onChange={e => setPurchaseVehicle(e.target.value)}
                  >
                    {classList.map(cls => (
                      <MenuItem key={cls.name} value={cls.name}>
                        {cls.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={12 / 5}>
                  <CurrencyInput
                    fullWidth
                    size="small"
                    value={newOrder.GrossMargin ?? 0}
                    onChange={value => setNewOrder(prev => ({ ...prev, GrossMargin: value }))}
                    label="Gross Margin"
                  />
                </Grid>
                <Grid size={12 / 5}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    value={newOrder.SetAside?.split(',') ?? []}
                    label="Set Aside"
                    slotProps={{
                      select: {
                        multiple: true,
                        onChange: e =>
                          setNewOrder(prev => ({ ...prev, SetAside: (e.target.value as string[]).join(',') })),
                      },
                    }}
                  >
                    {setAsides.map(option => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={12 / 5}>
                  <DatePicker
                    fullWidth
                    size="small"
                    label="Delivery Due Date"
                    value={newOrder.DeliveryDueDate ? dayjs.utc(newOrder.DeliveryDueDate) : null}
                    onChange={value => setNewOrder(prev => ({ ...prev, DeliveryDueDate: value?.toDate() ?? null }))}
                  />
                </Grid>
                <Grid size={12 / 5}>
                  <FormControlLabel
                    control={<Checkbox />}
                    label="Partial Invoice?"
                    checked={newOrder.PartialInvoicing}
                    onChange={(_e, checked) => setNewOrder(prev => ({ ...prev, PartialInvoicing: checked }))}
                  />
                </Grid>
                <Grid size={12 / 5}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="Contract #"
                    value={newOrder.ContractNumber ?? ''}
                    onChange={e =>
                      setPurchaseVehicle(
                        classList.find(
                          cls =>
                            cls.id ===
                            classList
                              .map(
                                cls =>
                                  cls.children?.find(c => c.availableonheader === 'T') ?? {
                                    ...cls,
                                    parent: cls.id,
                                  },
                              )
                              .find(cls => cls.name === e.target.value)?.parent,
                        )?.name ?? '',
                      )
                    }
                  >
                    {classList
                      .map(cls => cls.children?.find(c => c.availableonheader === 'T') ?? cls)
                      .map(cls => (
                        <MenuItem key={cls.name} value={cls.name}>
                          {cls.name}
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>
                <Grid size={12 / 5} />
                <Grid size={12 / 5}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="Product Type"
                    value={newOrder.ProductType ?? ''}
                    onChange={e => setNewOrder(prev => ({ ...prev, ProductType: e.target.value }))}
                  >
                    {productTypes.map(option => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={12 / 5}>
                  <FormControlLabel
                    control={<Checkbox />}
                    label="Risk Buy"
                    checked={newOrder.RiskBuy}
                    onChange={(_e, checked) => setNewOrder(prev => ({ ...prev, RiskBuy: checked }))}
                  />
                </Grid>
                <Grid size={12 / 5}>
                  <FormControlLabel
                    control={<Checkbox />}
                    label="Contains Service?"
                    checked={newOrder.ContainsService ?? false}
                    onChange={(_e, checked) => setNewOrder(prev => ({ ...prev, ContainsService: checked }))}
                  />
                </Grid>
                <Grid size={12 / 5}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="Division"
                    value={newOrder.Division ?? ''}
                    onChange={e => setNewOrder(prev => ({ ...prev, Division: e.target.value }))}
                  >
                    {divisionList.map(option => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={12 / 5} />
                <Grid size={12 / 5}>
                  <DatePicker
                    fullWidth
                    size="small"
                    label="Award Date"
                    value={dayjs.utc(newOrder.AwardDate)}
                    onChange={value => setNewOrder(prev => ({ ...prev, AwardDate: value?.toDate() ?? null }))}
                  />
                </Grid>
                <Grid size={12 / 5}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    value={newOrder.UrgentReason ?? ''}
                    onChange={e =>
                      setNewOrder(prev => ({
                        ...prev,
                        Urgent: e.target.value !== '',
                        UrgentReason: e.target.value === '' ? null : e.target.value,
                      }))
                    }
                    label="Urgent Reason *"
                  >
                    <MenuItem value="">Not Urgent</MenuItem>
                    <MenuItem value="PoP">PoP</MenuItem>
                    <MenuItem value="Cost Implications">Cost Implications</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={12 / 5}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="Security Requirement"
                    value={newOrder.SecurityRequirement ?? ''}
                    onChange={e => setNewOrder(prev => ({ ...prev, SecurityRequirement: e.target.value }))}
                  >
                    {securityRequirementOptions.map(option => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={12 / 5}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="End User"
                    value={newOrder.EndUserType ?? ''}
                    onChange={e => setNewOrder(prev => ({ ...prev, EndUserType: e.target.value }))}
                  >
                    {endUserList.map(option => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          <Accordion defaultExpanded disableGutters>
            <AccordionSummary>
              <Typography variant="h6">Billing and Shipping Details / Miscellaneous</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing="56px">
                <Grid size={4} className="order-details-edit-shipping-billing-container">
                  <div className="order-details-edit-shipping-billing-header">
                    <Typography variant="h6">Billing</Typography>
                  </div>
                  <Stack spacing="16px">
                    <TextField
                      fullWidth
                      size="small"
                      label="Attention"
                      value={newOrder.BillToAttention}
                      onChange={e => setNewOrder(prev => ({ ...prev, BillToAttention: e.target.value }))}
                      slotProps={{ htmlInput: { maxLength: fieldLengths.orders.billToAttention } }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Addressee (Company)"
                      value={newOrder.BillToCompany}
                      onChange={e => setNewOrder(prev => ({ ...prev, BillToCompany: e.target.value }))}
                      slotProps={{ htmlInput: { maxLength: fieldLengths.orders.billToCompany } }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Address Line 1"
                      value={newOrder.BillToAddress1}
                      onChange={e => setNewOrder(prev => ({ ...prev, BillToAddress1: e.target.value }))}
                      slotProps={{ htmlInput: { maxLength: fieldLengths.orders.billToAddress1 } }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Address Line 2"
                      value={newOrder.BillToAddress2}
                      onChange={e => setNewOrder(prev => ({ ...prev, BillToAddress2: e.target.value }))}
                      slotProps={{ htmlInput: { maxLength: fieldLengths.orders.billToAddress2 } }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="City"
                      value={newOrder.BillToAddressCity}
                      onChange={e => setNewOrder(prev => ({ ...prev, BillToAddressCity: e.target.value }))}
                      slotProps={{ htmlInput: { maxLength: fieldLengths.orders.billToAddressCity } }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="State"
                      value={newOrder.BillToAddressState}
                      onChange={e => setNewOrder(prev => ({ ...prev, BillToAddressState: e.target.value }))}
                      slotProps={{ htmlInput: { maxLength: fieldLengths.orders.billToAddressState } }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Postal Code / Zip"
                      value={newOrder.BillToAddressZip}
                      onChange={e => setNewOrder(prev => ({ ...prev, BillToAddressZip: e.target.value }))}
                      slotProps={{ htmlInput: { maxLength: fieldLengths.orders.billToAddressZip } }}
                    />
                    <Autocomplete
                      options={countryList.map(({ name }) => name)}
                      size="small"
                      value={newOrder.BillToAddressCountry ?? ''}
                      onChange={(_e, value) => {
                        setNewOrder(prev => ({ ...prev, BillToAddressCountry: value }))
                      }}
                      renderInput={params => <TextField {...params} label="Country" />}
                    />
                  </Stack>
                </Grid>
                <Grid size={8} className="order-details-edit-shipping-billing-container">
                  <div className="order-details-edit-shipping-billing-header">
                    <Typography variant="h6">Shipping</Typography>
                  </div>
                  <Grid container spacing="48px">
                    <Grid size={4}>
                      <Stack spacing="16px">
                        <TextField
                          fullWidth
                          size="small"
                          label="Attention"
                          value={newOrder.ShipToAttention}
                          onChange={e => setNewOrder(prev => ({ ...prev, ShipToAttention: e.target.value }))}
                          slotProps={{ htmlInput: { maxLength: fieldLengths.orders.shipToAttention } }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Addressee (Company)"
                          value={newOrder.ShipToCompany}
                          onChange={e => setNewOrder(prev => ({ ...prev, ShipToCompany: e.target.value }))}
                          slotProps={{ htmlInput: { maxLength: fieldLengths.orders.shipToCompany } }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Address Line 1"
                          value={newOrder.ShipToAddress1}
                          onChange={e => setNewOrder(prev => ({ ...prev, ShipToAddress1: e.target.value }))}
                          slotProps={{ htmlInput: { maxLength: fieldLengths.orders.shipToAddress1 } }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Address Line 2"
                          value={newOrder.ShipToAddress2}
                          onChange={e => setNewOrder(prev => ({ ...prev, ShipToAddress2: e.target.value }))}
                          slotProps={{ htmlInput: { maxLength: fieldLengths.orders.shipToAddress2 } }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="City"
                          value={newOrder.ShipToAddressCity}
                          onChange={e => setNewOrder(prev => ({ ...prev, ShipToAddressCity: e.target.value }))}
                          slotProps={{ htmlInput: { maxLength: fieldLengths.orders.shipToAddressCity } }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="State"
                          value={newOrder.ShipToAddressState}
                          onChange={e => setNewOrder(prev => ({ ...prev, ShipToAddressState: e.target.value }))}
                          slotProps={{ htmlInput: { maxLength: fieldLengths.orders.shipToAddressState } }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Postal Code / Zip"
                          value={newOrder.ShipToAddressZip}
                          onChange={e => setNewOrder(prev => ({ ...prev, ShipToAddressZip: e.target.value }))}
                          slotProps={{ htmlInput: { maxLength: fieldLengths.orders.shipToAddressZip } }}
                        />
                        <Autocomplete
                          options={countryList.map(({ name }) => name)}
                          size="small"
                          value={newOrder.ShipToAddressCountry ?? ''}
                          onChange={(_e, value) => {
                            setNewOrder(prev => ({ ...prev, ShipToAddressCountry: value }))
                          }}
                          renderInput={params => <TextField {...params} label="Country" />}
                        />
                      </Stack>
                    </Grid>
                    <Grid size={4}>
                      <Stack spacing="16px">
                        <TextField
                          fullWidth
                          size="small"
                          label="Receiver Contact Name"
                          value={newOrder.ReceiverContactName}
                          onChange={e => setNewOrder(prev => ({ ...prev, ReceiverContactName: e.target.value }))}
                          slotProps={{ htmlInput: { maxLength: fieldLengths.orders.receiverContactName } }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Receiver Contact Email"
                          value={newOrder.ReceiverContactEmail}
                          onChange={e => setNewOrder(prev => ({ ...prev, ReceiverContactEmail: e.target.value }))}
                          slotProps={{ htmlInput: { maxLength: fieldLengths.orders.receiverContactEmail } }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Receiver Contact Phone"
                          value={newOrder.ReceiverContactPhone}
                          onChange={e => setNewOrder(prev => ({ ...prev, ReceiverContactPhone: e.target.value }))}
                          slotProps={{ htmlInput: { maxLength: fieldLengths.orders.receiverContactPhone } }}
                        />
                        <FormControlLabel
                          control={<Checkbox />}
                          label="License POC same as Default Receiving POC"
                          checked={licenseSameAsReceiver}
                          onChange={(_e, checked) => {
                            setLicenseSameAsReceiver(checked)
                            if (checked) {
                              setNewOrder(prev => ({
                                ...prev,
                                LicenseContactName: prev.ReceiverContactName,
                                LicenseContactEmail: prev.ReceiverContactEmail,
                                LicenseContactPhone: prev.ReceiverContactPhone,
                              }))
                            } else {
                              setNewOrder(prev => ({
                                ...prev,
                                LicenseContactName: null,
                                LicenseContactEmail: null,
                                LicenseContactPhone: null,
                              }))
                            }
                          }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="License Contact Name"
                          value={newOrder.LicenseContactName ?? ''}
                          onChange={e => setNewOrder(prev => ({ ...prev, LicenseContactName: e.target.value }))}
                          slotProps={{ htmlInput: { maxLength: fieldLengths.orders.licenseContactName } }}
                          disabled={licenseSameAsReceiver}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="License Contact Email"
                          value={newOrder.LicenseContactEmail ?? ''}
                          onChange={e => setNewOrder(prev => ({ ...prev, LicenseContactEmail: e.target.value }))}
                          slotProps={{ htmlInput: { maxLength: fieldLengths.orders.licenseContactEmail } }}
                          disabled={licenseSameAsReceiver}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="License Contact Phone"
                          value={newOrder.LicenseContactPhone ?? ''}
                          onChange={e => setNewOrder(prev => ({ ...prev, LicenseContactPhone: e.target.value }))}
                          slotProps={{ htmlInput: { maxLength: fieldLengths.orders.licenseContactPhone } }}
                          disabled={licenseSameAsReceiver}
                        />
                      </Stack>
                    </Grid>
                    <Grid size={4}>
                      <Stack spacing="16px">
                        <CurrencyInput
                          fullWidth
                          size="small"
                          label="Shipping Charges"
                          value={newOrder.ShippingCharges ?? 0}
                          onChange={value => setNewOrder(prev => ({ ...prev, ShippingCharges: value }))}
                        />
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          className="order-details-edit-textarea"
                          size="small"
                          label="Delivery Instructions"
                          value={newOrder.DeliveryInstructions}
                          onChange={e => setNewOrder(prev => ({ ...prev, DeliveryInstructions: e.target.value }))}
                          slotProps={{ htmlInput: { maxLength: fieldLengths.orders.deliveryInstructions } }}
                        />
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          className="order-details-edit-textarea"
                          size="small"
                          label="Special Order/PO Instructions"
                          value={newOrder.SpecialInstructions}
                          onChange={e => setNewOrder(prev => ({ ...prev, SpecialInstructions: e.target.value }))}
                          slotProps={{ htmlInput: { maxLength: fieldLengths.orders.specialInstructions } }}
                        />
                      </Stack>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </div>
        <Dialog
          open={purchaseVehicle !== newOrder.PurchaseVehicle && purchaseVehicle !== ''}
          onClose={() => setPurchaseVehicle('')}
        >
          <DialogTitle variant="h6">Purchase Vehicle and Contract Number Warning</DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              Changing this will update the purchase vehicle and contract number for all line items. Are you sure you
              want to continue?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setPurchaseVehicle('')}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                updateOrderAndGrossMargin({
                  PurchaseVehicle: purchaseVehicle,
                  ContractNumber:
                    classList
                      .find(cls => cls.name === purchaseVehicle)
                      ?.children?.find(c => c.availableonheader === 'T')?.name ?? purchaseVehicle,
                })
                setPurchaseVehicle('')
              }}
            >
              Continue
            </Button>
          </DialogActions>
        </Dialog>
      </Dialog>
      <Snackbar open={saved === 'error'}>
        <Alert severity="error" variant="filled" onClose={() => setSaved('')}>
          There was a problem saving the edits. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
      <Snackbar open={saved === 'success'} autoHideDuration={5000} onClose={() => setSaved('')}>
        <Alert severity="success" variant="filled">
          Edits saved successfully.
        </Alert>
      </Snackbar>
    </>
  )
}
